"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { useOrganization } from "@clerk/nextjs"

import { useCoviAnalytics } from "../hooks/useCoviAnalytics"
import { useCoviHistorySnapshot } from "../hooks/useCoviHistorySnapshot"
import { useCoviHomeSurface } from "../hooks/useCoviHomeSurface"
import { useCoviOpenRequests } from "../hooks/useCoviOpenRequests"
import { useCoviThreadActivity } from "../hooks/useCoviThreadActivity"
import { useEphemeralThread } from "../hooks/useEphemeralThread"
import { usePanelWidth } from "../hooks/usePanelWidth"
import type { CoviChatItem, CoviThreadMeta } from "../model/types"
import {
  deleteOrgThreadState,
  loadOrgState,
  saveOrgState,
} from "../utils/storage"
import {
  loadThreads,
  newThreadMeta,
  pruneThreads,
  saveThreads,
} from "../utils/storageThreads"
import { isThreadEngaged } from "../utils/threadEngagement"
import { deriveThreadTitle } from "../utils/threadTitle"
import type {
  CoviCloseVia,
  CoviContextValue,
  CoviOpenVia,
  OpenThreadOptions,
} from "./CoviContext.types"
import { nextThreadId } from "./sofiaThreadState"

// Deliberately NOT org-scoped: whether the copilot panel chrome is open is a
// per-browser UI preference, not org data.
const PANEL_OPEN_STORAGE_KEY = "coval:copilot:panel-open"

const CoviContext = createContext<CoviContextValue | null>(null)

export function CoviProvider({ children }: { children: ReactNode }) {
  const { trackPanelOpened, trackPanelClosed } = useCoviAnalytics()
  const [isOpen, setIsOpen] = useState(false)
  const [homeReturnPath, setHomeReturnPath] = useState<string | null>(null)
  // Mirror of isOpen for the open/close/toggle controls: they decide whether a
  // genuine transition happened (and thus whether to emit a usage event) BEFORE
  // calling setState, keeping the state updater a pure function — safe under
  // StrictMode's double-invoked updaters.
  const isOpenRef = useRef(false)
  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])
  // The panel stays open until the user closes it — including across full
  // page loads and any shell remount, not just client-side navigation. The
  // open flag hydrates from localStorage post-mount (SSR-safe) and persists
  // on every change after hydration.
  const [openHydrated, setOpenHydrated] = useState(false)
  const { organization } = useOrganization()
  const orgSlug = organization?.slug ?? null

  // --- Thread state ---
  //
  // The panel holds many threads and shows one. This provider owns only the
  // thread REGISTRY (metadata + active id); each thread's items live under a
  // per-thread storage slot and are hydrated by that thread's own useCoviChat
  // instance (loadThreadItems / persistThreadItems below), so a user with
  // fifty threads pays for exactly the ones that are mounted.
  //
  // The registry state carries the org it belongs to so `threads`/`activeThreadId`
  // and their org can never desync — the persist effect writes only when the
  // registry's own org matches the active org, which closes the org-switch race
  // (a stale org's threads written under the new org's key).
  interface RegistryState {
    orgSlug: string | null
    threads: CoviThreadMeta[]
    activeThreadId: string | null
    migrationComplete: boolean
  }
  const [registry, setRegistry] = useState<RegistryState>({
    orgSlug: null,
    threads: [],
    activeThreadId: null,
    migrationComplete: true,
  })
  const threads = registry.threads
  const activeThreadId = registry.activeThreadId
  const {
    streamingThreadIds,
    setThreadStreaming,
    draftThreadIds,
    setThreadDraft,
    residentThreadIds,
  } = useCoviThreadActivity(threads, activeThreadId)
  // Per-thread seed prompts consumed by the thread's panel view on mount
  // (e.g. a page-summary thread opened programmatically). Held in a ref so
  // consumption is atomic; the panel keys its auto-send effect on the count.
  const threadSeedsRef = useRef<Map<string, string>>(new Map())

  // The thread a page opened on trial, kept only if the user answers it.
  //
  // Destructured, never used as an object: every one of these is identity-stable
  // while the trial itself changes as it runs. Depending on the whole thing
  // would rebuild the callbacks below on every trial transition — which is not
  // cosmetic, since effects keyed on their identity re-run their CLEANUP. That
  // is exactly how a summary thread got discarded in the same commit that
  // created it, and how a thread view would re-seed mid-conversation.
  const {
    threadId: trialThreadId,
    begin: beginTrial,
    read: readTrial,
    record: recordTrial,
    end: endTrial,
    reset: resetTrial,
  } = useEphemeralThread()

  // Whether the thread on screen is the user's conversation rather than one a
  // page opened. Gates every automatic takeover of the active thread.
  const threadEngaged = isThreadEngaged(threads, activeThreadId, trialThreadId)

  // Update only THIS org's registry — a no-op if the org has since changed, so
  // a late setter from a previous org can never clobber the new org's threads.
  const updateRegistry = useCallback(
    (
      forOrg: string | null,
      updater: (prev: RegistryState) => RegistryState,
    ) => {
      setRegistry((prev) => (prev.orgSlug === forOrg ? updater(prev) : prev))
    },
    [],
  )

  // Re-hydrate whenever the active org changes so one org's threads never leak
  // into another. Adopting the org into the registry state itself (not a ref)
  // keeps the persist effect from ever writing stale threads under the new
  // org's key.
  useEffect(() => {
    if (registry.orgSlug === orgSlug) {
      return
    }
    threadSeedsRef.current.clear()
    resetTrial()
    setHomeReturnPath(null)
    if (!orgSlug) {
      setRegistry({
        orgSlug: null,
        threads: [],
        activeThreadId: null,
        migrationComplete: true,
      })
      return
    }
    const loaded = loadThreads(orgSlug)
    if (loaded.threads.length > 0) {
      setRegistry({
        orgSlug,
        threads: loaded.threads,
        activeThreadId: loaded.activeThreadId ?? loaded.threads[0].id,
        migrationComplete: loaded.migrationComplete,
      })
      return
    }
    // Nothing stored for this org — start one empty thread. (loadThreads has
    // already adopted a pre-thread transcript and any archived conversations.)
    const id = nextThreadId()
    setRegistry({
      orgSlug,
      threads: [newThreadMeta(id, Date.now())],
      activeThreadId: id,
      migrationComplete: loaded.migrationComplete,
    })
  }, [orgSlug, registry.orgSlug, resetTrial, setHomeReturnPath])

  // Deployed environments restore the latest completed conversation into a
  // completely blank browser for any authenticated org. Existing local state
  // always wins.
  useCoviHistorySnapshot({
    enabled:
      process.env.NEXT_PUBLIC_VERCEL_ENV === "preview" ||
      process.env.NEXT_PUBLIC_VERCEL_ENV === "production",
    orgSlug,
    registry,
    setRegistry,
  })

  // Persist the registry whenever it changes so reopening the panel — or a full
  // reload — restores the same threads and selection. Only writes once the
  // registry has adopted the CURRENT org (registry.orgSlug === orgSlug), so it
  // never persists a stale org's threads mid-switch. Re-writing the just-hydrated
  // registry is idempotent, so no first-pass guard is needed.
  useEffect(() => {
    if (
      registry.orgSlug !== orgSlug ||
      !registry.migrationComplete ||
      !orgSlug
    ) {
      return
    }
    // A thread on trial is written out of the registry, not into it — that is
    // what makes a reload land on the threads the user actually has. Selection
    // falls back to nothing, which hydration reads as "first thread".
    const persistedThreads = trialThreadId
      ? threads.filter((thread) => thread.id !== trialThreadId)
      : threads
    if (persistedThreads.length === 0) {
      return
    }
    saveThreads(orgSlug, {
      activeThreadId: activeThreadId === trialThreadId ? null : activeThreadId,
      threads: persistedThreads,
    })
  }, [
    orgSlug,
    registry.orgSlug,
    registry.migrationComplete,
    threads,
    activeThreadId,
    trialThreadId,
  ])

  const loadThreadItems = useCallback(
    (threadId: string): CoviChatItem[] =>
      readTrial(threadId) ?? loadOrgState(orgSlug, threadId).items,
    [orgSlug, readTrial],
  )

  const persistThreadItems = useCallback(
    (threadId: string, items: CoviChatItem[]) => {
      // A thread still on trial keeps its transcript in memory instead.
      if (recordTrial(threadId, items)) {
        saveOrgState(orgSlug, { items }, threadId)
      }
      // Refresh the thread's derived metadata. Only rewrite when something the
      // list renders actually changed, so an idempotent persist (e.g. the
      // hydrate echo) never churns the registry into a new identity — this is
      // load-bearing for the per-thread persist↔hydrate loop guard.
      updateRegistry(orgSlug, (prev) => {
        const idx = prev.threads.findIndex((t) => t.id === threadId)
        if (idx === -1) {
          return prev
        }
        const current = prev.threads[idx]
        const nextTitle = current.titlePinned
          ? current.title
          : deriveThreadTitle(items)
        const messageCountChanged = current.messageCount !== items.length
        if (!messageCountChanged && current.title === nextTitle) {
          return prev
        }
        const nextThreads = [...prev.threads]
        nextThreads[idx] = {
          ...current,
          title: nextTitle,
          messageCount: items.length,
          updatedAt: messageCountChanged ? Date.now() : current.updatedAt,
        }
        return { ...prev, threads: nextThreads }
      })
    },
    [orgSlug, recordTrial, updateRegistry],
  )

  const { width, setWidth, isResizing, setResizing } = usePanelWidth()

  useEffect(() => {
    try {
      setIsOpen(window.localStorage.getItem(PANEL_OPEN_STORAGE_KEY) === "true")
    } catch {
      // ignore storage failures
    }
    setOpenHydrated(true)
  }, [])

  useEffect(() => {
    if (!openHydrated) {
      return
    }
    try {
      // Write only on actual change so users who never interact with the panel
      // get zero storage writes.
      const stored = window.localStorage.getItem(PANEL_OPEN_STORAGE_KEY)
      if (stored !== String(isOpen) && (stored !== null || isOpen)) {
        window.localStorage.setItem(PANEL_OPEN_STORAGE_KEY, String(isOpen))
      }
    } catch {
      // ignore storage failures
    }
  }, [isOpen, openHydrated])

  // Bumps whenever a thread seed is queued so a thread's panel view re-runs its
  // auto-send effect (the seed itself is read atomically from threadSeedsRef).
  const [seedNonce, setSeedNonce] = useState(0)

  // open/close/toggle track ONLY on a genuine state transition (decided from
  // isOpenRef before setState), so re-opening an already-open panel — or the
  // header toggle that always opens — never double-counts a session boundary.
  const open = useCallback(
    (via: CoviOpenVia = "toggle") => {
      if (!isOpenRef.current) {
        isOpenRef.current = true
        trackPanelOpened(via)
        setIsOpen(true)
      }
    },
    [trackPanelOpened],
  )
  const close = useCallback(
    (via: CoviCloseVia = "close_button") => {
      if (isOpenRef.current) {
        isOpenRef.current = false
        trackPanelClosed(via)
        setIsOpen(false)
      }
    },
    [trackPanelClosed],
  )
  const toggle = useCallback(
    (via: Extract<CoviOpenVia, CoviCloseVia> = "shortcut") => {
      if (isOpenRef.current) {
        close(via)
      } else {
        open(via)
      }
    },
    [close, open],
  )

  // Read-and-clear a thread's queued seed (atomic — fires at most once).
  const consumeThreadSeed = useCallback((threadId: string): string | null => {
    const seed = threadSeedsRef.current.get(threadId) ?? null
    threadSeedsRef.current.delete(threadId)
    return seed
  }, [])

  // --- Thread operations ---

  const setActiveThread = useCallback(
    (threadId: string) => {
      updateRegistry(orgSlug, (prev) =>
        prev.activeThreadId === threadId ||
        !prev.threads.some((t) => t.id === threadId)
          ? prev
          : { ...prev, activeThreadId: threadId },
      )
    },
    [orgSlug, updateRegistry],
  )

  const openThread = useCallback(
    (opts?: OpenThreadOptions): string => {
      const id = nextThreadId()
      const now = Date.now()
      const seed = opts?.seedPrompt?.trim()
      if (seed) {
        threadSeedsRef.current.set(id, seed)
        setSeedNonce((nonce) => nonce + 1)
      }
      const seedItems = opts?.seedItems
      // Write the seeded transcript to the thread's own slot BEFORE the
      // registry update makes it active: the panel view hydrates from storage
      // on mount, so persisting first means it renders the seeded items on its
      // very first paint instead of flashing an empty thread. A thread on trial
      // gets the same head start from memory, which loadThreadItems reads first.
      const supersededTrialId = opts?.ephemeral
        ? beginTrial(id, seedItems ?? [])
        : null
      if (!opts?.ephemeral && seedItems && seedItems.length > 0) {
        saveOrgState(orgSlug, { items: seedItems }, id)
      }
      updateRegistry(orgSlug, (prev) => {
        const protectedThreadIds = new Set(opts?.protectedThreadIds)
        // A page-triggered thread is not an explicit storage-management action,
        // so never let it silently prune an existing conversation.
        if (opts?.preserveExistingThreads) {
          for (const thread of prev.threads) {
            protectedThreadIds.add(thread.id)
          }
        }
        if (prev.activeThreadId) {
          protectedThreadIds.add(prev.activeThreadId)
        }
        protectedThreadIds.add(id)
        const meta = newThreadMeta(id, now)
        // One trial at a time: a new page's summary retires the last page's
        // untouched thread rather than stacking beside it.
        const surviving = supersededTrialId
          ? prev.threads.filter((thread) => thread.id !== supersededTrialId)
          : prev.threads
        const { threads, evicted } = pruneThreads(
          [
            ...surviving,
            opts?.title
              ? {
                  ...meta,
                  title: opts.title,
                  // Auto-titling reads the first user message; a seeded thread
                  // may never get one, so pin the title to stop the next
                  // persist reverting it to "New chat".
                  titlePinned: true,
                  messageCount: seedItems?.length ?? 0,
                }
              : { ...meta, messageCount: seedItems?.length ?? 0 },
          ],
          protectedThreadIds,
        )
        for (const evictedId of evicted) {
          deleteOrgThreadState(orgSlug, evictedId)
        }
        return { ...prev, threads, activeThreadId: id }
      })
      return id
    },
    [beginTrial, orgSlug, updateRegistry],
  )

  const deleteThread = useCallback(
    (threadId: string) => {
      updateRegistry(orgSlug, (prev) => {
        if (!prev.threads.some((thread) => thread.id === threadId)) {
          return prev
        }
        deleteOrgThreadState(orgSlug, threadId)
        const remaining = prev.threads.filter(
          (thread) => thread.id !== threadId,
        )
        // Never leave the panel threadless — deleting the last one starts a fresh thread.
        if (remaining.length === 0) {
          const id = nextThreadId()
          return {
            ...prev,
            threads: [newThreadMeta(id, Date.now())],
            activeThreadId: id,
          }
        }
        // Reselect only when the deleted thread was the one on screen, and land
        // on the most recently updated survivor — the one the list shows first.
        const activeThreadId =
          prev.activeThreadId === threadId
            ? [...remaining].sort((a, b) => b.updatedAt - a.updatedAt)[0].id
            : prev.activeThreadId
        return { ...prev, threads: remaining, activeThreadId }
      })
    },
    [orgSlug, updateRegistry],
  )

  // Nothing of a trial thread was ever written, so discarding is only registry
  // surgery — deleteThread also reselects when it was the thread on screen.
  const discardEphemeralThread = useCallback(() => {
    const discarded = endTrial()
    if (discarded) {
      deleteThread(discarded)
    }
  }, [deleteThread, endTrial])

  const renameThread = useCallback(
    (threadId: string, title: string) => {
      const trimmed = title.trim()
      if (!trimmed) {
        return
      }
      updateRegistry(orgSlug, (prev) => ({
        ...prev,
        threads: prev.threads.map((t) =>
          t.id === threadId
            ? { ...t, title: trimmed.slice(0, 80), titlePinned: true }
            : t,
        ),
      }))
    },
    [orgSlug, updateRegistry],
  )

  const threadAppendersRef = useRef(
    new Map<string, (items: CoviChatItem[]) => void>(),
  )
  const registerThreadAppender = useCallback(
    (threadId: string, append: (items: CoviChatItem[]) => void) => {
      threadAppendersRef.current.set(threadId, append)
      return () => {
        if (threadAppendersRef.current.get(threadId) === append) {
          threadAppendersRef.current.delete(threadId)
        }
      }
    },
    [],
  )
  const appendThreadItems = useCallback(
    (threadId: string, items: CoviChatItem[]) => {
      const append = threadAppendersRef.current.get(threadId)
      if (!append) {
        return false
      }
      append(items)
      return true
    },
    [],
  )

  const toggleThreadPinned = useCallback(
    (threadId: string) => {
      updateRegistry(orgSlug, (prev) => ({
        ...prev,
        threads: prev.threads.map((thread) =>
          thread.id === threadId
            ? { ...thread, pinned: !thread.pinned }
            : thread,
        ),
      }))
    },
    [orgSlug, updateRegistry],
  )

  const setThreadUnread = useCallback(
    (threadId: string, unread: boolean) => {
      updateRegistry(orgSlug, (prev) => {
        const index = prev.threads.findIndex((thread) => thread.id === threadId)
        if (index === -1 || prev.threads[index].unread === unread) {
          return prev
        }
        const nextThreads = [...prev.threads]
        nextThreads[index] = { ...nextThreads[index], unread }
        return { ...prev, threads: nextThreads }
      })
    },
    [orgSlug, updateRegistry],
  )

  const {
    showThreadOnHome,
    startNewChatOnHome,
    showDailyBriefing,
    openHome,
    returnHomeThreadToPanel,
    closeHomeThread,
  } = useCoviHomeSurface({
    orgSlug,
    homeReturnPath,
    setHomeReturnPath,
    residentThreadIds,
    open,
    close,
    openThread,
    setActiveThread,
  })

  const { openWithPrompt, openWithSummary } = useCoviOpenRequests({
    open,
    openHome,
    openThread,
    showThreadOnHome,
    residentThreadIds,
  })

  const value = useMemo<CoviContextValue>(
    () => ({
      isOpen,
      open,
      openWithPrompt,
      openWithSummary,
      openHome,
      showThreadOnHome,
      startNewChatOnHome,
      showDailyBriefing,
      returnHomeThreadToPanel,
      closeHomeThread,
      homeReturnPath,
      close,
      toggle,
      seedNonce,
      consumeThreadSeed,
      orgSlug,
      threads,
      activeThreadId,
      threadEngaged,
      setActiveThread,
      openThread,
      deleteThread,
      discardEphemeralThread,
      renameThread,
      toggleThreadPinned,
      appendThreadItems,
      registerThreadAppender,
      setThreadUnread,
      streamingThreadIds,
      setThreadStreaming,
      draftThreadIds,
      setThreadDraft,
      residentThreadIds,
      loadThreadItems,
      persistThreadItems,
      width,
      setWidth,
      isResizing,
      setResizing,
    }),
    [
      isOpen,
      open,
      openWithPrompt,
      openWithSummary,
      openHome,
      showThreadOnHome,
      startNewChatOnHome,
      showDailyBriefing,
      returnHomeThreadToPanel,
      closeHomeThread,
      homeReturnPath,
      close,
      toggle,
      seedNonce,
      consumeThreadSeed,
      orgSlug,
      threads,
      activeThreadId,
      threadEngaged,
      setActiveThread,
      openThread,
      deleteThread,
      discardEphemeralThread,
      renameThread,
      toggleThreadPinned,
      appendThreadItems,
      registerThreadAppender,
      setThreadUnread,
      streamingThreadIds,
      setThreadStreaming,
      draftThreadIds,
      setThreadDraft,
      residentThreadIds,
      loadThreadItems,
      persistThreadItems,
      width,
      setWidth,
      isResizing,
      setResizing,
    ],
  )

  return <CoviContext.Provider value={value}>{children}</CoviContext.Provider>
}

export function useCovi(): CoviContextValue {
  const ctx = useContext(CoviContext)
  if (!ctx) {
    throw new Error("useCovi must be used within a CoviProvider")
  }
  return ctx
}

/**
 * Non-throwing variant for consumers that live outside the Sofia module and
 * may render without a provider (e.g. the app shell's header toggle, which is
 * shared across pages and unit-tested in isolation). Returns null when there is
 * no CoviProvider above.
 */
export function useCoviOptional(): CoviContextValue | null {
  return useContext(CoviContext)
}
